(function () {
  function parseJson(el, fallback) {
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return fallback;
    }
  }

  function formatMoney(cents) {
    if (typeof window.themeFormatMoney === "function") {
      return window.themeFormatMoney(cents);
    }
    return (Number(cents) / 100).toFixed(2).replace(".", ",");
  }

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector("[data-quantity-input]");
      this.minus = this.querySelector('[name="minus"]');
      this.plus = this.querySelector('[name="plus"]');
      if (!this.input) return;

      this.minus?.addEventListener("click", () => this.change(-1));
      this.plus?.addEventListener("click", () => this.change(1));
    }

    change(delta) {
      const min = Number(this.input.min) || 1;
      const next = Math.max(min, Number(this.input.value || min) + delta);
      this.input.value = String(next);
      this.input.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  class ProductMedia extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector("[data-product-media-main]");
      this.thumbs = this.querySelectorAll(".product-media__thumb");
      this.media = parseJson(this.querySelector("[data-product-media-json]"), []);
      this.prevBtn = this.querySelector("[data-product-media-prev]");
      this.nextBtn = this.querySelector("[data-product-media-next]");
      this.carousel = this.querySelector("[data-product-media-carousel]");
      this.thumbsRail = this.querySelector("[data-product-media-thumbs]");
      this.zoomEnabled = this.dataset.imageZoom === "true";
      this.zoomScale = 2.5;
      this.activeZoomRoot = null;
      this._zoomPointerId = null;
      this._zoomStartX = 0;
      this._zoomStartY = 0;
      this._zoomMoved = false;
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onZoomKeydown = this.onZoomKeydown.bind(this);
      this.onZoomPointerDown = this.onZoomPointerDown.bind(this);
      this.onZoomPointerMove = this.onZoomPointerMove.bind(this);
      this.onZoomPointerUp = this.onZoomPointerUp.bind(this);
      this.onZoomPointerCancel = this.onZoomPointerCancel.bind(this);

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener("click", (event) => {
          if (this._thumbsDragMoved) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          this.setActiveMedia(thumb.dataset.mediaId);
        });
      });

      this.bindThumbsDragScroll();

      this.prevBtn?.addEventListener("click", () => this.navigate(-1));
      this.nextBtn?.addEventListener("click", () => this.navigate(1));

      if (this.zoomEnabled) {
        this.addEventListener("pointerdown", this.onZoomPointerDown);
        this.addEventListener("pointermove", this.onZoomPointerMove);
        this.addEventListener("pointerup", this.onZoomPointerUp);
        this.addEventListener("pointercancel", this.onZoomPointerCancel);
      }

      this.carousel?.addEventListener("theme-swiper:slideChange", (event) => {
        const mediaId = event.detail?.element?.querySelector(
          ".swiper-slide-active[data-media-id]"
        )?.dataset?.mediaId;
        if (mediaId) this.setActiveMedia(mediaId, { syncCarousel: false });
        this.resetZoom();
      });

      this.addEventListener("product:variant-change", (event) => {
        const mediaId = event.detail?.featuredMediaId;
        if (mediaId) this.setActiveMedia(String(mediaId));
      });
    }

    disconnectedCallback() {
      this.resetZoom();
      if (this.zoomEnabled) {
        this.removeEventListener("pointerdown", this.onZoomPointerDown);
        this.removeEventListener("pointermove", this.onZoomPointerMove);
        this.removeEventListener("pointerup", this.onZoomPointerUp);
        this.removeEventListener("pointercancel", this.onZoomPointerCancel);
      }
    }

    bindThumbsDragScroll() {
      const rail = this.thumbsRail;
      if (!rail || rail.dataset.dragScrollBound === "true") return;
      rail.dataset.dragScrollBound = "true";

      const DRAG_THRESHOLD = 6;
      let pointerId = null;
      let startX = 0;
      let startScroll = 0;
      let dragging = false;
      this._thumbsDragMoved = false;

      rail.addEventListener("pointerdown", (event) => {
        if (event.button !== 0) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startScroll = rail.scrollLeft;
        dragging = false;
        this._thumbsDragMoved = false;
      });

      rail.addEventListener("pointermove", (event) => {
        if (pointerId == null || event.pointerId !== pointerId) return;
        const dx = event.clientX - startX;

        if (!dragging) {
          if (Math.abs(dx) < DRAG_THRESHOLD) return;
          dragging = true;
          this._thumbsDragMoved = true;
          rail.classList.add("is-dragging");
          try {
            rail.setPointerCapture(pointerId);
          } catch (_err) {
            /* ignore */
          }
        }

        rail.scrollLeft = startScroll - dx;
        event.preventDefault();
      });

      const endDrag = (event) => {
        if (pointerId == null || event.pointerId !== pointerId) return;
        const wasDragging = dragging;
        pointerId = null;
        dragging = false;
        rail.classList.remove("is-dragging");
        try {
          rail.releasePointerCapture(event.pointerId);
        } catch (_err) {
          /* ignore */
        }
        if (wasDragging) {
          // Suppress the click that follows a real drag.
          window.setTimeout(() => {
            this._thumbsDragMoved = false;
          }, 0);
        } else {
          this._thumbsDragMoved = false;
        }
      };

      rail.addEventListener("pointerup", endDrag);
      rail.addEventListener("pointercancel", endDrag);
    }

    getSizedSrc(item, width) {
      const base = item.preview_image?.src || item.src || "";
      if (!base) return "";

      const src = base.startsWith("//") ? `https:${base}` : base;

      try {
        const url = new URL(src, window.location.origin);
        url.searchParams.set("width", String(width));
        return url.toString();
      } catch {
        const joiner = src.includes("?") ? "&" : "?";
        return `${src}${joiner}width=${width}`;
      }
    }

    getZoomSrc(item) {
      return this.getSizedSrc(item, 1600);
    }

    getDisplaySrc(item) {
      return this.getSizedSrc(item, 1200);
    }

    buildSrcset(item, widths) {
      return widths
        .map((w) => {
          const url = this.getSizedSrc(item, w);
          return url ? `${url} ${w}w` : "";
        })
        .filter(Boolean)
        .join(", ");
    }

    updateZoomPan(zoomRoot, event) {
      const image = zoomRoot.querySelector(".product-media__image");
      if (!image) return;

      const rect = zoomRoot.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const px = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
      const py = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));

      image.style.transformOrigin = `${px}% ${py}%`;
      image.style.transform = `scale(${this.zoomScale})`;
    }

    setCarouselTouch(allow) {
      const swiper = this.carousel?.swiper;
      if (!swiper) return;
      swiper.allowTouchMove = allow;
    }

    activateZoom(zoomRoot, event) {
      if (!zoomRoot) return;

      this.deactivateZoom();
      this.activeZoomRoot = zoomRoot;
      zoomRoot.classList.add("is-zoomed");
      this.setCarouselTouch(false);
      this.updateZoomPan(zoomRoot, event);
      document.addEventListener("click", this.onDocumentClick);
      document.addEventListener("keydown", this.onZoomKeydown);
    }

    deactivateZoom(zoomRoot) {
      const target = zoomRoot || this.activeZoomRoot;
      if (!target) return;

      target.classList.remove("is-zoomed");
      const image = target.querySelector(".product-media__image");
      if (image) {
        image.style.transform = "";
        image.style.transformOrigin = "";
      }

      if (this.activeZoomRoot === target) {
        this.activeZoomRoot = null;
      }

      this.setCarouselTouch(true);
      document.removeEventListener("click", this.onDocumentClick);
      document.removeEventListener("keydown", this.onZoomKeydown);
    }

    onZoomPointerDown(event) {
      if (!this.zoomEnabled || event.button != null && event.button !== 0) return;
      const zoomRoot = event.target.closest("[data-product-media-zoom]");
      if (!zoomRoot) return;

      this._zoomPointerId = event.pointerId;
      this._zoomStartX = event.clientX;
      this._zoomStartY = event.clientY;
      this._zoomMoved = false;

      if (this.activeZoomRoot === zoomRoot) {
        try {
          zoomRoot.setPointerCapture(event.pointerId);
        } catch (_err) {
          /* ignore */
        }
      }
    }

    onZoomPointerMove(event) {
      if (!this.zoomEnabled || this._zoomPointerId !== event.pointerId) return;

      const dx = Math.abs(event.clientX - this._zoomStartX);
      const dy = Math.abs(event.clientY - this._zoomStartY);
      if (dx > 8 || dy > 8) this._zoomMoved = true;

      if (!this.activeZoomRoot) return;
      if (event.target.closest("[data-product-media-zoom]") !== this.activeZoomRoot &&
          this.activeZoomRoot.hasPointerCapture?.(event.pointerId) !== true) {
        return;
      }

      event.preventDefault();
      this.updateZoomPan(this.activeZoomRoot, event);
    }

    onZoomPointerUp(event) {
      if (!this.zoomEnabled || this._zoomPointerId !== event.pointerId) return;

      const zoomRoot = event.target.closest("[data-product-media-zoom]") || this.activeZoomRoot;
      const moved = this._zoomMoved;
      this._zoomPointerId = null;
      this._zoomMoved = false;

      if (!zoomRoot) return;

      if (this.activeZoomRoot === zoomRoot) {
        if (!moved) this.deactivateZoom(zoomRoot);
        return;
      }

      // Ignore swipe gestures meant for the carousel / page scroll.
      if (moved) return;

      event.preventDefault();
      event.stopPropagation();
      this.activateZoom(zoomRoot, event);
    }

    onZoomPointerCancel(event) {
      if (this._zoomPointerId !== event.pointerId) return;
      this._zoomPointerId = null;
      this._zoomMoved = false;
    }

    onDocumentClick(event) {
      if (!this.activeZoomRoot) return;
      if (event.target.closest("[data-product-media-zoom]") === this.activeZoomRoot) return;
      this.deactivateZoom();
    }

    onZoomKeydown(event) {
      if (event.key === "Escape" && this.activeZoomRoot) {
        event.preventDefault();
        this.deactivateZoom();
      }
    }

    resetZoom() {
      this.deactivateZoom();
    }

    renderMainMedia(item, mediaId) {
      const alt = (item.alt || "").replace(/"/g, "&quot;");
      const mediaType = item.media_type || "image";
      const sizes = "(min-width: 990px) min(50vw, 720px), min(92vw, 21rem)";
      const widths = [360, 480, 640, 800, 1000, 1200];

      if (mediaType === "image") {
        const displaySrc = this.getDisplaySrc(item);
        if (!displaySrc) return;

        const srcset = this.buildSrcset(item, widths);
        const zoomSrc = this.getZoomSrc(item);
        const imgAttrs = `src="${displaySrc}"${srcset ? ` srcset="${srcset}"` : ""} sizes="${sizes}" alt="${alt}" class="product-media__image" loading="lazy" decoding="async"`;

        if (this.zoomEnabled) {
          this.main.innerHTML = `<div class="product-media__main" data-media-id="${mediaId}" data-media-type="image"><div class="product-media__zoom" data-product-media-zoom><picture class="product-media__picture"><img ${imgAttrs} data-zoom-src="${zoomSrc}"></picture></div></div>`;
        } else {
          this.main.innerHTML = `<div class="product-media__main" data-media-id="${mediaId}" data-media-type="image"><picture class="product-media__picture"><img ${imgAttrs}></picture></div>`;
        }
        return;
      }

      const displaySrc = this.getDisplaySrc(item);
      if (!displaySrc) return;

      this.main.innerHTML = `<div class="product-media__main" data-media-id="${mediaId}" data-media-type="${mediaType}"><img src="${displaySrc}" alt="${alt}" class="product-media__image" loading="lazy" decoding="async"></div>`;
    }

    getActiveIndex() {
      const mainItem = this.main?.querySelector("[data-media-id]");
      if (mainItem?.dataset.mediaId) {
        const index = this.media.findIndex(
          (entry) => String(entry.id) === String(mainItem.dataset.mediaId)
        );
        if (index >= 0) return index;
      }

      const activeThumb = this.querySelector(".product-media__thumb.is-active");
      if (activeThumb?.dataset.mediaPosition !== undefined) {
        return Number(activeThumb.dataset.mediaPosition);
      }

      return 0;
    }

    navigate(delta) {
      if (this.media.length < 2) return;
      const nextIndex =
        (this.getActiveIndex() + delta + this.media.length) % this.media.length;
      const item = this.media[nextIndex];
      if (item) this.setActiveMedia(String(item.id));
    }

    setActiveMedia(mediaId, options = {}) {
      if (!mediaId || !this.main) return;
      const item = this.media.find((entry) => String(entry.id) === String(mediaId));
      if (!item) return;

      const syncCarousel = options.syncCarousel !== false;
      const mediaIndex = this.media.findIndex(
        (entry) => String(entry.id) === String(mediaId)
      );

      this.thumbs.forEach((thumb) => {
        const active = thumb.dataset.mediaId === String(mediaId);
        thumb.classList.toggle("is-active", active);
        thumb.setAttribute("aria-current", active ? "true" : "false");
        if (active && typeof thumb.scrollIntoView === "function") {
          thumb.scrollIntoView({ inline: "nearest", block: "nearest", behavior: "smooth" });
        }
      });

      if (syncCarousel && this.carousel?.swiper && mediaIndex >= 0) {
        this.carousel.swiper.slideTo(mediaIndex);
      }

      this.resetZoom();
      this.renderMainMedia(item, mediaId);
    }
  }

  function initStickyAtc(section) {
    const sticky = section.querySelector("[data-product-sticky-atc]");
    const buyAnchor = section.querySelector("[data-product-buy-anchor]");
    const stickySubmit = section.querySelector("[data-product-sticky-submit]");
    const stickySubmitText = section.querySelector("[data-product-sticky-submit-text]");
    const mainSubmit = section.querySelector("[data-product-submit]");
    const mainSubmitText = section.querySelector("[data-product-submit-text]");

    if (!sticky || !buyAnchor || !mainSubmit) return null;

    const syncStickySubmit = () => {
      if (!stickySubmit) return;
      stickySubmit.disabled = mainSubmit.disabled;
      if (stickySubmitText && mainSubmitText) {
        stickySubmitText.textContent = mainSubmitText.textContent;
      }
    };

    const syncStickyAtcOffset = (visible = sticky.classList.contains("product-sticky-atc--visible")) => {
      const root = document.documentElement;
      if (!visible) {
        root.classList.remove("has-sticky-atc");
        root.style.setProperty("--sticky-atc-offset", "0px");
        return;
      }
      const height = Math.ceil(sticky.getBoundingClientRect().height);
      root.classList.add("has-sticky-atc");
      root.style.setProperty("--sticky-atc-offset", `${Math.max(height, 0)}px`);
    };

    const setStickyVisible = (visible) => {
      sticky.classList.toggle("product-sticky-atc--visible", visible);
      sticky.setAttribute("aria-hidden", visible ? "false" : "true");
      if (visible) {
        sticky.removeAttribute("inert");
      } else {
        sticky.setAttribute("inert", "");
      }
      syncStickyAtcOffset(visible);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const buyVisible = entries.some((entry) => entry.isIntersecting);
        setStickyVisible(!buyVisible);
      },
      { threshold: 0 }
    );

    observer.observe(buyAnchor);
    syncStickySubmit();
    syncStickyAtcOffset(false);

    let resizeObserver = null;
    if (typeof ResizeObserver === "function") {
      resizeObserver = new ResizeObserver(() => syncStickyAtcOffset());
      resizeObserver.observe(sticky);
    }

    stickySubmit?.addEventListener("click", () => {
      if (mainSubmit.disabled) return;
      mainSubmit.click();
    });

    return {
      syncStickySubmit,
      disconnect: () => {
        observer.disconnect();
        resizeObserver?.disconnect();
        document.documentElement.classList.remove("has-sticky-atc");
        document.documentElement.style.setProperty("--sticky-atc-offset", "0px");
      },
    };
  }

  function initProductSection(section) {
    const variants = parseJson(section.querySelector("[data-product-variants]"), []);
    const variantInput = section.querySelector("[data-product-variant-id]");
    const optionInputs = section.querySelectorAll("[data-product-variant-picker] input[type='radio']");
    const priceRoot = section.querySelector("[data-product-price]");
    const inventoryRoot = section.querySelector("[data-product-inventory]");
    const inventoryText = section.querySelector("[data-product-inventory-text]");
    const submitBtn = section.querySelector("[data-product-submit]");
    const submitText = section.querySelector("[data-product-submit-text]");
    const stickyAtc = initStickyAtc(section);
    const popustPercent = Number(section.dataset.popustPercent) || 0;
    const showSavingsBadge = section.dataset.showSavingsBadge !== "false";
    const saveLabelTemplate = section.dataset.saveLabel || "Ušteda PERCENT%";

    if (!variantInput || !variants.length) return;

    function selectedOptions() {
      const options = [null, null, null];
      const checkedInputs = section.querySelectorAll(
        "[data-product-variant-picker] .product-option:not(.product-option--fixed) input[type='radio']:checked"
      );
      checkedInputs.forEach((input) => {
        const position = Number(input.dataset.optionPosition);
        if (position >= 1 && position <= 3) {
          options[position - 1] = input.value;
        }
      });
      return options;
    }

    function findVariant() {
      const options = selectedOptions();
      return variants.find((variant) => {
        return options.every((value, index) => {
          if (!value) return true;
          if (index === 0) return variant.option1 === value;
          if (index === 1) return variant.option2 === value;
          if (index === 2) return variant.option3 === value;
          return true;
        });
      });
    }

    function resolveSalePricing(variant) {
      const price = Number(variant.price) || 0;
      const compareAt = Number(variant.compare_at_price) || 0;

      // Delikatesi: popust_XX tag takes priority over compare_at (same as product-sale-pricing.liquid)
      if (popustPercent > 0) {
        const salePrice = Math.round((price * (100 - popustPercent)) / 100);
        return {
          onSale: true,
          originalPrice: price,
          salePrice,
          discountPercent: popustPercent,
        };
      }

      if (compareAt > price) {
        return {
          onSale: true,
          originalPrice: compareAt,
          salePrice: price,
          discountPercent: Math.round(((compareAt - price) * 100) / compareAt),
        };
      }

      return {
        onSale: false,
        originalPrice: price,
        salePrice: price,
        discountPercent: 0,
      };
    }

    function updatePrice(variant) {
      if (!priceRoot || !variant) return;
      const pricing = resolveSalePricing(variant);
      const badgeLabel = saveLabelTemplate.replace("PERCENT", String(pricing.discountPercent));

      if (pricing.onSale) {
        priceRoot.innerHTML = `
          <div class="product-price">
            <p class="p4 product-price__compare"><s>${formatMoney(pricing.originalPrice)}</s></p>
            <div class="product-price__row">
              <p class="h3 product-price__sale">${formatMoney(pricing.salePrice)}</p>
              ${
                showSavingsBadge && pricing.discountPercent > 0
                  ? `<span class="b2 product-price__badge">${badgeLabel}</span>`
                  : ""
              }
            </div>
          </div>`;
      } else {
        priceRoot.innerHTML = `<div class="product-price"><p class="h3 product-price__regular">${formatMoney(pricing.salePrice)}</p></div>`;
      }
    }

    function updateInventory(variant) {
      if (!inventoryRoot || !inventoryText || !variant) return;
      inventoryRoot.classList.toggle("product-inventory--in-stock", variant.available);
      inventoryRoot.classList.toggle("product-inventory--out-of-stock", !variant.available);
      inventoryText.textContent = variant.available
        ? inventoryRoot.dataset.inStock || "Na stanju, spremno za slanje"
        : inventoryRoot.dataset.outOfStock || "Nema na stanju";
    }

    function updateVariant(variant) {
      if (!variant) return;
      variantInput.value = variant.id;
      updatePrice(variant);
      updateInventory(variant);

      if (submitBtn) {
        submitBtn.disabled = !variant.available;
      }
      if (submitText) {
        submitText.textContent = variant.available
          ? submitBtn?.dataset.addLabel || "Dodaj u korpu"
          : submitBtn?.dataset.soldOutLabel || "Rasprodato";
      }

      stickyAtc?.syncStickySubmit();

      // Keep GTM data-settings in sync with the selected variant
      if (section.dataset.settings) {
        try {
          const gtm = JSON.parse(section.dataset.settings);
          gtm.item_id = variant.id;
          gtm.item_sku = variant.sku || "";
          gtm.item_price = (Number(variant.price) || 0) / 100;
          section.dataset.settings = JSON.stringify(gtm);
        } catch (_) {
          /* ignore */
        }
      }

      section.querySelector("product-media")?.dispatchEvent(
        new CustomEvent("product:variant-change", {
          bubbles: false,
          detail: { featuredMediaId: variant.featured_media?.id || variant.featured_image?.id },
        })
      );

      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({}, "", url.toString());
    }

    if (inventoryRoot) {
      const inStock = inventoryText?.textContent?.trim();
      if (inStock) inventoryRoot.dataset.inStock = inStock;
      inventoryRoot.dataset.outOfStock = "Nema na stanju";
    }

    if (submitBtn) {
      submitBtn.dataset.addLabel = submitText?.textContent?.trim() || "Dodaj u korpu";
      submitBtn.dataset.soldOutLabel = "Rasprodato";
    }

    // Price/inventory updates only on user variant change — first paint stays Liquid.
    optionInputs.forEach((input) => {
      input.addEventListener("change", () => {
        const variant = findVariant();
        if (variant) updateVariant(variant);
      });
    });
  }

  function boot(root = document) {
    root.querySelectorAll("[data-product-section]").forEach(initProductSection);
  }

  if (!customElements.get("quantity-input")) {
    customElements.define("quantity-input", QuantityInput);
  }
  if (!customElements.get("product-media")) {
    customElements.define("product-media", ProductMedia);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot());
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target?.matches?.("[data-product-section]")) {
      boot(event.target);
    }
  });
})();
